import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '@entities/auth';
import {
  bootstrapAttribution,
  identifyAnalytics,
  initAnalytics,
  resetIdentityDedup,
  setAnalyticsUser,
  trackPageView,
} from '@shared/lib/analytics';
import {
  getAnonymousIdPreloadScript,
  getGtmInlineScript,
  isAnalyticsEnabled,
} from '@shared/lib/analytics/config';
import { sanitizeAnalyticsPath } from '@shared/lib/analytics/sanitize-location';

const injectHeadScript = (id: string, content: string): void => {
  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.text = content;
  document.head.appendChild(script);
};

export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const previousPath = useRef(location.pathname);
  const bootstrapped = useRef(false);

  useEffect(() => {
    if (bootstrapped.current || !isAnalyticsEnabled()) {
      return;
    }

    bootstrapped.current = true;
    injectHeadScript('analytics-anonymous-id', getAnonymousIdPreloadScript());
    injectHeadScript('google-tag-manager', getGtmInlineScript());

    bootstrapAttribution();
    initAnalytics();
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setAnalyticsUser(user ? { id: user.id } : null);
    resetIdentityDedup();
    identifyAnalytics();
  }, [user, isLoading]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const path = sanitizeAnalyticsPath(location.pathname);
    const prev = previousPath.current;
    trackPageView(prev !== path ? { previous_path: sanitizeAnalyticsPath(prev) } : undefined);
    previousPath.current = path;
  }, [location.pathname, isLoading]);

  return children;
};
